/**
 * Replace picture (components/OutfitPictureReplace.tsx; owner, 2026-11-01: "a small Gyro Admin feature that swaps an
 * outfit's picture in place; reusable for future art fixes"): the body PUT /v1/admin/desktop/mascot/outfits/:id/image
 * takes, its answer, and the action's words. The service keeps everything else about the outfit and checks the picture
 * as it checks an added one (services/gyro-svc/src/desktopMascot.ts replaceOutfitPicture).
 */
import { mascotErrorCode, mascotErrorText, type AdminOutfit } from './desktopMascot';
import type { OutfitFacingView } from './outfitFacing';

/** The picture, and the revisions the card loaded: the catalog's, and the annex's for an outfit kept beyond the catalog. */
export interface ReplacePictureBody {
  pngBase64: string;
  expectedRevision: number;
  expectedAnnexRevision?: number;
}

/** The service's answer: the outfit, its image before and after, and which way the new one faces. */
export interface ReplacePictureAnswer {
  outfitId: string;
  placement: 'catalog' | 'overflow';
  /** False when the PNG sent is already the outfit's picture: nothing was changed. */
  changed: boolean;
  previousSha256: string;
  sha256: string;
  /** Absent when nothing was changed. */
  facing?: OutfitFacingView;
  strippedChunks: string[];
}

/** The action's tooltip, and what it keeps. */
export const REPLACE_PICTURE_HINT = 'Swap this outfit’s picture for a new PNG. Its name, dates, category, ratings, owners and animations stay as they are.';

/** The body for `outfit`: the annex revision goes too for an outfit kept beyond the catalog (its change is the annex's). */
export function replacePictureBody(pngBase64: string, outfit: Pick<AdminOutfit, 'placement'>, revision: number, annexRevision: number | null | undefined): ReplacePictureBody {
  return outfit.placement === 'overflow' && typeof annexRevision === 'number'
    ? { pngBase64, expectedRevision: revision, expectedAnnexRevision: annexRevision }
    : { pngBase64, expectedRevision: revision };
}

/** The question under the preview, before Confirm. */
export function confirmPictureText(name: string): string {
  return `Replace the picture of “${name}” with this one? Everything else about the outfit stays as it is.`;
}

/** The note after the service took the picture. */
export function replacedPictureText(name: string, answer: Pick<ReplacePictureAnswer, 'changed' | 'facing'>): string {
  if (!answer.changed) return `That PNG is already the picture of “${name}”, so nothing was changed.`;
  const mirrored = answer.facing?.mirrored ? ' It was drawn facing right, so it is stored mirrored to face left (Undo flip puts it back as drawn).' : '';
  return `“${name}” has its new picture.${mirrored} The change is in the audit trail; desktops and the gallery show it after their next check, within about 20 minutes.`;
}

/** Refusals after which the picture is saved all the same: the card reads the catalog again and the form closes. */
const SAVED_ANYWAY = ['mascot_audit_pending', 'mascot_audit_failed', 'mascot_facing_unsaved'];

export function pictureSavedAnyway(error: unknown): boolean {
  return SAVED_ANYWAY.includes(mascotErrorCode(error) ?? '');
}

/**
 * The note for a refused Replace picture: a picture another outfit already has, in this action's words (the add's words
 * tell the admin not to add it twice); every other refusal as the card words it (mascotErrorText), the service's own
 * sentence for a picture the desktop rules refuse.
 */
export function replacePictureErrorText(error: unknown, outfits: ReadonlyArray<Pick<AdminOutfit, 'id' | 'name'>>): string {
  if (mascotErrorCode(error) === 'mascot_duplicate') {
    const body = (error as { body?: Record<string, unknown> } | null | undefined)?.body;
    const existing = outfits.find((outfit) => outfit.id === body?.outfitId);
    const named = existing ? ` “${existing.name}”` : '';
    return body?.retired === true
      ? `This picture is the retired outfit${named}, so it cannot be this outfit’s too. Choose another PNG. Nothing was changed.`
      : `This picture is already the outfit${named}, so it cannot be this outfit’s too. Choose another PNG. Nothing was changed.`;
  }
  return mascotErrorText(error, outfits, 'The picture was not replaced.');
}
